// Zweck: Typisierte HubSpot-API-Aufrufe und Deal-Mapping.
// Kontext: One360-Client für Projekte, Eigenschaften und Dokumentverknüpfungen.
// Stand: 2026-10-05
import axios from 'axios';
import one360 from './one360';
import { isSessionError } from '../sessionErrors';
import {
  HubSpotDealsResponse,
  HubSpotDeal,
  DealContactListItem,
} from '../../types/hubspot';

const DEALS_PAGE_TIMEOUT_MS = 20_000;
const DEALS_SEARCH_TIMEOUT_MS = 65_000;
// Die Doku-App dokumentiert sowohl die operative WP-Pipeline als auch die intern
// angelegten Sales-Projekte. One360 verarbeitet die IDs als OR-Filter.
const DOCUMENTATION_PIPELINE_IDS = ['482384628', 'default'] as const;

import type { GetDealsOptions } from '../../interfaces/deals';

// One360 gibt Deals mit verschachtelten `properties: { dealname, dealstage, ... }` zurück.
// Das Frontend erwartet diese Felder flach auf Deal-Ebene → hier mappen.
function flattenDeal(raw: unknown): HubSpotDeal {
  const { id, properties, createdAt, updatedAt, archived, ...rest } = (raw ?? {}) as Record<string, unknown>;
  return { id, createdAt, updatedAt, archived, ...rest, ...((properties ?? {}) as Record<string, unknown>) } as unknown as HubSpotDeal;
}

export async function getDealsAsync(options?: GetDealsOptions): Promise<HubSpotDealsResponse> {
  const params: Record<string, string> = {};
  params['pipelineId'] = DOCUMENTATION_PIPELINE_IDS.join(',');
  if (options?.refresh) params['refresh'] = '1';
  if (options?.refreshNacharbeiten) params['refreshNacharbeiten'] = '1';
  if (options?.refreshWartung) params['refreshWartung'] = '1';
  if (typeof options?.limit === 'number') params['limit'] = String(options.limit);
  if (options?.after) params['after'] = options.after;
  if (options?.q?.trim()) params['q'] = options.q.trim();
  if (options?.stageLabel?.trim()) params['stageLabel'] = options.stageLabel.trim();
  if (options?.nacharbeiten === 'yes' || options?.nacharbeiten === 'no') params['nacharbeiten'] = options.nacharbeiten;
  if (options?.wartung === 'yes' || options?.wartung === 'no') params['wartung'] = options.wartung;

  const isSearchRequest = Boolean(options?.q && options.q.trim().length >= 3);
  const timeoutMs = isSearchRequest ? DEALS_SEARCH_TIMEOUT_MS : DEALS_PAGE_TIMEOUT_MS;

  const headers = options?.refresh || options?.bypassCache ? { 'X-One360-No-Cache': 'true' } : undefined;

  try {
    const raw = await one360.get<{ deals: unknown[]; after?: string; hasMore?: boolean }>(
      '/api/v1/hubspot/deals',
      { params, headers, timeout: timeoutMs, signal: options?.signal }
    );
    return {
      deals: (raw.deals ?? []).map(flattenDeal),
      after: raw.after,
      hasMore: raw.hasMore,
    };
  } catch (err: unknown) {
    if (axios.isCancel(err)) throw err;
    console.error('[HubSpot] getDeals fehlgeschlagen:', err);
    throw new Error('Deals konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
  }
}

export async function getDealByIdAsync(dealId: string, options?: { bypassCache?: boolean; propagateSessionError?: boolean }): Promise<HubSpotDeal | null> {
  const id = dealId.trim();
  if (!id) return null;
  const headers = options?.bypassCache ? { 'X-One360-No-Cache': 'true' } : undefined;
  try {
    const raw = await one360.get<unknown>(`/api/v1/hubspot/deals/${encodeURIComponent(id)}`, { headers });
    return flattenDeal(raw);
  } catch (err: unknown) {
    if (options?.propagateSessionError && isSessionError(err)) throw err;
    console.error('[HubSpot] getDealById fehlgeschlagen:', err);
    return null;
  }
}

export async function getDealContactsAsync(dealId: string, options?: { bypassCache?: boolean }): Promise<DealContactListItem[]> {
  const id = dealId.trim();
  if (!id) return [];
  const headers = options?.bypassCache ? { 'X-One360-No-Cache': 'true' } : undefined;
  try {
    const result = await one360.get<DealContactListItem[] | { contacts: DealContactListItem[] }>(
      `/api/v1/hubspot/deals/${encodeURIComponent(id)}/contacts`,
      { headers }
    );
    return Array.isArray(result) ? result : result?.contacts ?? [];
  } catch (err: unknown) {
    console.error('[HubSpot] getDealContacts fehlgeschlagen:', err);
    return [];
  }
}

export async function uploadDocumentAsync(
  dealId: string,
  propertyName: string,
  file: File,
): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);
  try {
    const result = await one360.post<{ fileId?: string; id?: string }>(
      `/api/v1/hubspot/deals/${encodeURIComponent(dealId)}/documents/${encodeURIComponent(propertyName)}`,
      formData,
    );
    return result?.fileId ?? result?.id ?? '';
  } catch (err: unknown) {
    console.error('[HubSpot] uploadDocument fehlgeschlagen:', err);
    throw new Error('Die Datei konnte nicht hochgeladen werden. Bitte versuchen Sie es später erneut.');
  }
}

export async function resolveOwnersByEmailAsync(email: string): Promise<string[]> {
  const normalizedEmail = email.trim();
  if (!normalizedEmail) return [];
  try {
    const response = await one360.get<{ ownerIds?: string[] }>('/api/v1/hubspot/owners/resolve', {
      params: { email: normalizedEmail },
    });
    const ownerIds = Array.isArray(response.ownerIds) ? response.ownerIds : [];
    return ownerIds.map((id) => String(id || '').trim()).filter(Boolean);
  } catch (err: unknown) {
    console.error('[HubSpot] resolveOwners fehlgeschlagen:', err);
    return [];
  }
}

// HubSpot file IDs are numeric; Google Drive IDs are alphanumeric (33+ chars)
function isHubSpotFileId(fileId: string): boolean {
  return /^\d+$/.test(fileId.trim());
}

export async function getSignedUrlAsync(fileId: string): Promise<string> {
  try {
    if (isHubSpotFileId(fileId)) {
      const response = await one360.get<{ url: string }>(`/api/v1/hubspot/files/${encodeURIComponent(fileId)}/signed-url`);
      return response.url;
    }
    const response = await one360.get<{ url: string }>(`/api/v1/google/drive/link/${encodeURIComponent(fileId)}`);
    return response.url;
  } catch (err: unknown) {
    console.error('[HubSpot] getSignedUrl fehlgeschlagen:', err);
    throw new Error('Die Datei konnte nicht abgerufen werden. Bitte versuchen Sie es später erneut.');
  }
}

export async function updateTextPropertyAsync(
  dealId: string, property: string, value: string, operationId: string = crypto.randomUUID(),
): Promise<void> {
  // Derselbe Vorgang mit demselben Payload erhält auch nach einem Neustart denselben Key.
  // Ein neu gemergter Dateistand ist ein anderer Request und darf keinen Key-Konflikt erzeugen.
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(
    JSON.stringify([operationId, dealId, property, value]),
  ));
  const key = `doku-property-${Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('')}`;
  try {
    await one360.patch(`/api/v1/hubspot/deals/${encodeURIComponent(dealId)}/properties`, { Properties: { [property]: value } }, { headers: { 'Idempotency-Key': key } });
  } catch (err: unknown) {
    console.error('[HubSpot] updateTextProperty fehlgeschlagen:', err);
    // UI-Aufrufer erhalten verständlichen Text; Diagnose und Retry-Metadaten bleiben
    // separat erhalten. Auch strukturierte Fehlerobjekte unterstützen, niemals err mutieren.
    const details = typeof err === 'object' && err !== null
      ? err as { status?: unknown; code?: unknown; isAuthMissing?: unknown }
      : {};
    throw Object.assign(new Error('Die Eigenschaft konnte nicht gespeichert werden.'), {
      cause: err,
      status: typeof details.status === 'number' ? details.status : undefined,
      code: typeof details.code === 'string' ? details.code : undefined,
      isAuthMissing: details.isAuthMissing === true,
      isHubspotPropertyError: true,
    });
  }
}

export async function searchDealsAsync(query: string, options?: { signal?: AbortSignal }): Promise<HubSpotDeal[]> {
  const q = query.trim();
  if (q.length < 3) return [];
  try {
    // Eine Suche muss immer den aktuellen HubSpot-Bestand abfragen, nicht nur
    // den lokal geladenen Ausschnitt oder einen zwischengespeicherten Snapshot.
    const response = await getDealsAsync({ q, bypassCache: true, signal: options?.signal });
    return response.deals;
  } catch (err: unknown) {
    if (axios.isCancel(err)) throw err;
    const msg = (err instanceof Error ? err.message : String(err ?? '')).toLowerCase();
    if (msg.includes('abort') || msg.includes('cancel')) throw err;
    console.error('[HubSpot] searchDeals fehlgeschlagen:', err);
    throw new Error('Deal-Suche fehlgeschlagen.');
  }
}

export async function getNacharbeitenStatusAsync(projectId: string): Promise<import('../../types/hubspot').NacharbeitenStatusSnapshot> {
  try {
    return await one360.get<import('../../types/hubspot').NacharbeitenStatusSnapshot>(`/api/v1/ops/projects/${encodeURIComponent(projectId)}/nacharbeiten-status`);
  } catch (err: unknown) {
    console.error('[HubSpot] getNacharbeitenStatus fehlgeschlagen:', err);
    throw new Error('Nacharbeiten-Status konnte nicht geladen werden.');
  }
}

export async function getNacharbeitenTicketsAsync(dealId: string): Promise<import('../../types/hubspot').NacharbeitenTicketListItem[]> {
  try {
    return await one360.get<import('../../types/hubspot').NacharbeitenTicketListItem[]>(`/api/v1/ops/projects/${encodeURIComponent(dealId)}/nacharbeiten-tickets`);
  } catch (err: unknown) {
    console.error('[HubSpot] getNacharbeitenTickets fehlgeschlagen:', err);
    throw new Error('Nacharbeiten-Tickets konnten nicht geladen werden.');
  }
}

export async function getWartungStatusAsync(projectId: string): Promise<import('../../types/hubspot').WartungStatusSnapshot> {
  try {
    return await one360.get<import('../../types/hubspot').WartungStatusSnapshot>(`/api/v1/ops/projects/${encodeURIComponent(projectId)}/wartung-status`);
  } catch (err: unknown) {
    console.error('[HubSpot] getWartungStatus fehlgeschlagen:', err);
    throw new Error('Wartung-Status konnte nicht geladen werden.');
  }
}

export async function getWartungTicketsAsync(dealId: string): Promise<import('../../types/hubspot').WartungTicketListItem[]> {
  try {
    return await one360.get<import('../../types/hubspot').WartungTicketListItem[]>(`/api/v1/ops/projects/${encodeURIComponent(dealId)}/wartung-tickets`);
  } catch (err: unknown) {
    console.error('[HubSpot] getWartungTickets fehlgeschlagen:', err);
    throw new Error('Wartung-Tickets konnten nicht geladen werden.');
  }
}

// === Ticket #1090: Nacharbeiten-Kommentar-Thread ===

// Wirft absichtlich statt wie andere Listen-Getter in dieser Datei auf `[]` zu degradieren:
// ReworkCommentThread muss einen Ladefehler von "Ticket hat wirklich keine Kommentare"
// unterscheiden können, u.a. um nach einem bestätigten Kommentar-Sync nicht versehentlich
// alle bereits angezeigten echten Kommentare durch eine fälschlich leere Liste zu ersetzen
// (PR-Review-Finding). Einziger Aufrufer ist ReworkCommentThread.tsx.
export async function getReworkCommentsAsync(ticketId: string): Promise<import('../../types/hubspot').ReworkComment[]> {
  try {
    return await one360.get<import('../../types/hubspot').ReworkComment[]>(`/api/v1/ops/reworks/${encodeURIComponent(ticketId)}/comments`);
  } catch (err: unknown) {
    console.error('[HubSpot] getReworkComments fehlgeschlagen:', err);
    throw new Error('Kommentare konnten nicht geladen werden.');
  }
}

export async function addReworkCommentAsync(
  ticketId: string,
  payload: { text: string; mentionedOwnerIds: string[] },
): Promise<import('../../types/hubspot').ReworkComment> {
  try {
    return await one360.post<import('../../types/hubspot').ReworkComment>(
      `/api/v1/ops/reworks/${encodeURIComponent(ticketId)}/comments`,
      payload,
    );
  } catch (err: unknown) {
    console.error('[HubSpot] addReworkComment fehlgeschlagen:', err);
    // Der HTTP-Status muss erhalten bleiben (nicht durch einen neuen, statuslosen Error
    // ersetzt werden): queueService.processQueue entscheidet anhand von `error.status`,
    // ob ein fehlgeschlagenes Queue-Item permanent verworfen (4xx) oder mit erhöhtem
    // Retry-Zähler weiterhin versucht wird (PR-Review-Finding — ohne den Status wäre selbst
    // ein permanenter 4xx-Fehler fälschlich als transient behandelt worden).
    const status = (err as { status?: unknown } | null)?.status;
    throw Object.assign(
      new Error('Kommentar konnte nicht gespeichert werden. Bitte versuchen Sie es später erneut.'),
      typeof status === 'number' ? { status } : {}
    );
  }
}

export async function updateReworkCommentAsync(
  ticketId: string,
  commentId: string,
  payload: { text: string; mentionedOwnerIds: string[] },
): Promise<import('../../types/hubspot').ReworkComment> {
  try {
    return await one360.patch<import('../../types/hubspot').ReworkComment>(
      `/api/v1/ops/reworks/${encodeURIComponent(ticketId)}/comments/${encodeURIComponent(commentId)}`,
      payload,
    );
  } catch (err: unknown) {
    console.error('[HubSpot] updateReworkComment fehlgeschlagen:', err);
    throw new Error('Kommentar konnte nicht aktualisiert werden. Bitte versuchen Sie es später erneut.');
  }
}

export async function deleteReworkCommentAsync(ticketId: string, commentId: string): Promise<void> {
  try {
    await one360.delete<void>(`/api/v1/ops/reworks/${encodeURIComponent(ticketId)}/comments/${encodeURIComponent(commentId)}`);
  } catch (err: unknown) {
    console.error('[HubSpot] deleteReworkComment fehlgeschlagen:', err);
    throw new Error('Kommentar konnte nicht gelöscht werden. Bitte versuchen Sie es später erneut.');
  }
}

// Wirft absichtlich (wie getReworkCommentsAsync) statt auf `[]` zu degradieren: sonst
// bekäme der Aufrufer nie mit, dass @-Mentions/Hervorhebung für die ganze Session lautlos
// nicht funktionieren (PR-Review-Finding). Einziger Aufrufer ist ReworkCommentThread.tsx.
export async function getHubSpotOwnersAsync(): Promise<import('../../types/hubspot').HubSpotOwnerListItem[]> {
  try {
    return await one360.get<import('../../types/hubspot').HubSpotOwnerListItem[]>('/api/v1/hubspot/owners');
  } catch (err: unknown) {
    console.error('[HubSpot] getHubSpotOwners fehlgeschlagen:', err);
    throw new Error('HubSpot-User konnten nicht geladen werden.');
  }
}
